import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { AppConfig } from "../../../api/config/config";
import deleteIcon from "../../../assets/images/icons/delete_white.svg";
import editIcon from "../../../assets/images/icons/edit_white.svg";
import shareIcon from "../../../assets/images/icons/share-icon.svg";
import {
  FilePreview,
  GeneralButton,
  GeneralImage,
  Loader,
  Modal,
  ShareLinkComponent,
} from "../../../components";
import { EndPoints } from "../../../Constants/endpoints";
import { useGetData, useGetPersonalInfo, usePostData } from "../../../hooks";
import { GetAllCertificates } from "../../../interface/GeneralResponse";
import { useToast } from "../../../Services/ToastContext";
import { TitleWithArrow } from "../../../Share";
import EditDoctorCertificate from "./EditDoctorCertificate";

const DoctorCertificateDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isPending } = useGetData<GetAllCertificates>(
    EndPoints.GetCertificates,
    {},
    id,
  );

  const { refetch, isLoading } = useGetData<GetAllCertificates[]>(
    EndPoints.GetCertificates,
  );

  const details = data?.Data;
  const { data: userData } = useGetPersonalInfo();

  const [isFileOpen, setIsFileOpen] = useState<boolean>(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const handleShareClick = () => setIsShareModalOpen(true);
  const closeShareModal = () => setIsShareModalOpen(false);

  const openEditCertificateModal = () => {
    setIsEditModalOpen(true);
  };
  const closeEditCertificateModal = () => {
    setIsEditModalOpen(false);
    refetch();
  };
  const handleDeleteClick = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const { t } = useTranslation();

  const { mutate } = usePostData(`${EndPoints.DeleteCertificate}/${id}`);

  const handleDelete = async () => {
    try {
      mutate(
        {},
        {
          onSuccess: () => {
            navigate("/profile/doctor_certificate");
            showToast(t("deleted_successfully"), "success");
            refetch();
            queryClient.invalidateQueries({
              queryKey: [EndPoints.GetCertificates],
            });
          },
          onError: (error) => {
            showToast(error.message || t("failed_to_delete"), "error");
          },
        },
      );
    } catch (error) {
      showToast("An unexpected error occurred.", "error");
    }
  };

  return (
    <div className="box min-h-full w-full">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-5">
        <TitleWithArrow title="back_to_all_certificates" />
        <div className="flex flex-wrap items-center gap-4">
          <GeneralButton
            color="red"
            icon={deleteIcon}
            title={t("delete")}
            onClick={handleDeleteClick}
            className="!px-3 !py-2"
          />
          <GeneralButton
            color="yellow"
            icon={editIcon}
            title={t("edit")}
            onClick={openEditCertificateModal}
            className="!px-3 !py-2"
          />
          <GeneralButton
            color="gray"
            icon={shareIcon}
            title={t("share")}
            className="!px-3 !py-2"
            onClick={handleShareClick}
          />
        </div>
      </div>
      {isPending ? (
        <Loader />
      ) : (
        <div className="flex gap-5 max-lg:flex-col">
          <GeneralImage
            src={details?.Image}
            alt={details?.Name}
            className="max-h-[70dvh] max-w-96 cursor-pointer rounded-2xl"
            onClick={() => setIsFileOpen(true)}
          />
          <div className="w-full">
            <div className="flex flex-wrap items-center justify-between gap-5 text-lg font-bold">
              <h2>{details?.Name}</h2>
              <span>{details?.Date}</span>
            </div>
            <div className="text-body-text">
              <h3 className="my-4">{details?.Authority}</h3>
              <p>{details?.Description}</p>
            </div>
          </div>
        </div>
      )}
      <Modal header={t("delete")} isOpen={isModalOpen} onClose={closeModal}>
        <p className="text-lg text-secondaryDark">
          {t("are_you_sure_that_you_want_to_delete")}
        </p>
        <div className="mt-5 flex max-w-md gap-2 self-end">
          <GeneralButton
            fontColor="body-text"
            title={t("cancel")}
            color="lite"
            onClick={closeModal}
          />
          <GeneralButton
            type="submit"
            title={t("delete")}
            color="red"
            onClick={handleDelete}
          />
        </div>
      </Modal>
      <Modal
        header={t("edit_certificate")}
        isOpen={isEditModalOpen}
        onClose={closeEditCertificateModal} 
      >
        {id && (
          <EditDoctorCertificate
            id={id}
            onSuccess={closeEditCertificateModal}
          />
        )}
      </Modal>

      {isFileOpen && (
        <Modal
          header={details?.Name} 
          isOpen={isFileOpen}
          onClose={() => setIsFileOpen(false)}
        >
          <FilePreview
            isLoading={isLoading}
            type={"IMAGE"}
            url={AppConfig.SERVER_URL + details?.Image}
          />
        </Modal>
      )}

      <ShareLinkComponent
        shareLink={`${AppConfig.WEB_APP_URL}/search/${userData?.Data?.FullName.replaceAll(" ", "-")}/${userData?.Data?.FacilityId}/certificates/${details?.Id}?provider=${userData?.Data?.Id}`}
        open={isShareModalOpen}
        onClose={closeShareModal}
      />
    </div>
  );
};

export default DoctorCertificateDetails;
